import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { OriginalBadge } from './ContentCard';
import { useApp, fmtDuration } from '../context/AppContext';
import { useCatalog } from '../context/CatalogContext';
import { toast } from 'sonner';

export function HeroTitle({ title, size = 1 }) {
  const [first, ...rest] = title.split(' ');
  if (!rest.length) return <h1 className="font-serif-cine text-gold font-bold leading-[0.9]" style={{ fontSize: 76 * size }}>{title}</h1>;
  return (
    <h1 className="font-serif-cine text-gold font-bold leading-[0.9]">
      <span className="block" style={{ fontSize: 46 * size, lineHeight: 1 }}>{first}</span>
      <span className="block" style={{ fontSize: 72 * size, lineHeight: 0.95, marginTop: -2 }}>{rest.join(' ')}</span>
    </h1>
  );
}

export const MOTION_TYPES = ['slowZoomIn', 'slowZoomOut', 'panLeft', 'panRight', 'panUp', 'panDown', 'video', 'none'];
export const MOTION_LABELS = { slowZoomIn: 'Slow Zoom In', slowZoomOut: 'Slow Zoom Out', panLeft: 'Pan Left', panRight: 'Pan Right', panUp: 'Pan Up', panDown: 'Pan Down', video: 'Video', none: 'Static' };
export const SPEED_MS = { verySlow: 30000, slow: 24000, normal: 18000 };
const ROTATE_MS = 12000;
const MANUAL_PAUSE_MS = 20000;

export function slideActive(s) {
  if (s.enabled === false) return false;
  const now = Date.now();
  if (s.startDate && new Date(s.startDate).getTime() > now) return false;
  if (s.endDate && new Date(s.endDate).getTime() + 86400000 < now) return false;
  return true;
}

export function resolveSlide(s, c) {
  return {
    ...s,
    id: s.id || `hs-${s.contentId}`,
    content: c,
    title: s.title || c.title,
    subtitle: s.subtitle || (c.isOriginal ? 'CLAX ORIGINAL' : c.type === 'podcast' ? 'CLAX PODCAST' : ''),
    description: s.description || c.tagline,
    heroImage: s.heroImage || c.banner || c.image,
    heroVideo: s.heroVideo || c.heroVideo || null,
    mobileHeroImage: s.mobileHeroImage || s.heroImage || c.image,
    mobileHeroVideo: s.mobileHeroVideo || null,
    motionType: s.motionType || 'slowZoomIn',
    motionDuration: s.motionDuration || SPEED_MS[s.motionSpeed || 'slow'] || SPEED_MS.slow,
    focalPoint: s.focalPoint || '70% 20%',
    genres: s.genres || c.genres || [],
    year: s.year || c.year,
    ageRating: s.ageRating || c.rating,
  };
}

function useMedia(query) {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => { const mq = window.matchMedia(query); const h = (e) => setM(e.matches); mq.addEventListener('change', h); setM(mq.matches); return () => mq.removeEventListener('change', h); }, [query]);
  return m;
}

function allowMobileVideo() {
  const c = navigator.connection;
  if (!c) return true;
  if (c.saveData) return false;
  return !c.effectiveType || c.effectiveType === '4g';
}

export function HeroMedia({ slide, active, paused, reduced, isMobile, preload = false }) {
  const [videoReady, setVideoReady] = useState(false);
  const [mountVideo, setMountVideo] = useState(false);
  const vref = useRef(null);
  const image = isMobile ? slide.mobileHeroImage : slide.heroImage;
  const video = isMobile ? slide.mobileHeroVideo || (allowMobileVideo() ? slide.heroVideo : null) : slide.heroVideo;
  const useVideo = !!video && !reduced && slide.motionType !== 'none';
  const motion = reduced || slide.motionType === 'video' ? (reduced ? 'none' : 'slowZoomIn') : slide.motionType;

  useEffect(() => {
    if (!active || !useVideo) { setMountVideo(false); setVideoReady(false); return; }
    const t = setTimeout(() => setMountVideo(true), 1500);
    return () => clearTimeout(t);
  }, [active, useVideo, slide.id]);

  useEffect(() => {
    const v = vref.current; if (!v) return;
    if (paused || !active) v.pause(); else v.play().catch(() => {});
  }, [paused, active, mountVideo]);

  return (
    <>
      <img
        src={image}
        alt=""
        loading={preload ? 'eager' : 'lazy'}
        decoding="async"
        className={`hero-media m-${motion} ${paused ? 'paused' : ''}`}
        style={{ objectPosition: slide.focalPoint, animationDuration: `${slide.motionDuration}ms` }}
        data-testid={active ? 'hero-media-active' : undefined}
      />
      {mountVideo && (
        <div className={`hero-video-layer ${videoReady ? 'ready' : ''}`}>
          <video
            ref={vref}
            src={video}
            poster={image}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: slide.focalPoint }}
            data-testid="hero-video"
          />
        </div>
      )}
    </>
  );
}

export default function DynamicHero({ slides, compact = false }) {
  const navigate = useNavigate();
  const { openDetail, isAvailable } = useApp();
  const { byId } = useCatalog();
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const isMobile = useMedia('(max-width: 640px)');
  const items = useMemo(() => (slides || []).filter(slideActive).map((s) => ({ s, c: byId(s.contentId) })).filter((x) => x.c).map(({ s, c }) => resolveSlide(s, c)), [slides, byId]);

  const [idx, setIdx] = useState(0);
  const [prev, setPrev] = useState(null);
  const [cycle, setCycle] = useState(0);
  const [visible, setVisible] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const [manualUntil, setManualUntil] = useState(0);
  const ref = useRef(null);
  const touch = useRef(null);
  const n = items.length;

  const goTo = useCallback((i, manual = false) => {
    setIdx((cur) => { const nx = ((i % Math.max(1, n)) + n) % Math.max(1, n); if (nx !== cur) setPrev(cur); return nx; });
    setCycle((c) => c + 1);
    if (manual) setManualUntil(Date.now() + MANUAL_PAUSE_MS);
  }, [n]);
  const next = useCallback((m) => setIdx((cur) => { const nx = (cur + 1) % Math.max(1, n); setPrev(cur); setCycle((c) => c + 1); if (m) setManualUntil(Date.now() + MANUAL_PAUSE_MS); return nx; }), [n]);
  const back = useCallback((m) => setIdx((cur) => { const nx = (cur - 1 + n) % Math.max(1, n); setPrev(cur); setCycle((c) => c + 1); if (m) setManualUntil(Date.now() + MANUAL_PAUSE_MS); return nx; }), [n]);

  useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);
  useEffect(() => { if (prev === null) return; const t = setTimeout(() => setPrev(null), 1300); return () => clearTimeout(t); }, [prev, cycle]);

  const paused = !visible || !tabVisible;
  useEffect(() => {
    if (n < 2 || paused || reduced) return;
    const t = setInterval(() => { if (Date.now() >= manualUntil) next(false); }, ROTATE_MS);
    return () => clearInterval(t);
  }, [n, paused, reduced, manualUntil, next]);

  useEffect(() => {
    const el = ref.current; if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const h = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', h);
    return () => document.removeEventListener('visibilitychange', h);
  }, []);
  useEffect(() => {
    const h = (e) => { if (!visible || n < 2 || ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return; if (e.key === 'ArrowRight') next(true); if (e.key === 'ArrowLeft') back(true); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [visible, n, next, back]);

  if (!n) return null;
  const slide = items[idx] || items[0];
  const c = slide.content;
  const meta = [...slide.genres.slice(0, 2), String(slide.year), slide.ageRating].filter(Boolean);
  const nextIdx = (idx + 1) % n;

  const onPlay = () => {
    if (!isAvailable(c)) { toast.error('This title is not available in your region.'); return; }
    navigate(c.type === 'podcast' ? `/podcasts/${c.id}` : `/watch/${c.id}`);
  };
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => { if (touch.current === null) return; const dx = e.changedTouches[0].clientX - touch.current; touch.current = null; if (Math.abs(dx) > 50) (dx < 0 ? next(true) : back(true)); };

  return (
    <section ref={ref} className={`hero relative overflow-hidden select-none hero-h ${compact ? 'compact' : ''}`} data-testid="hero" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} aria-roledescription="carousel">
      {items.map((s, i) => {
        const isActive = i === idx, isPrev = i === prev, isNext = n > 1 && i === nextIdx;
        if (!isActive && !isPrev && !isNext) return null;
        return (
          <div key={s.id} className="hero-layer" style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 2 : isPrev ? 1 : 0, pointerEvents: 'none' }} data-testid={isActive ? 'hero-slide-active' : undefined} data-slide={s.id}>
            {(isActive || isPrev) ? <HeroMedia key={`${s.id}-${isActive ? cycle : 'p'}`} slide={s} active={isActive} paused={paused} reduced={reduced} isMobile={isMobile} /> : <img src={isMobile ? s.mobileHeroImage : s.heroImage} alt="" className="hero-media m-none" style={{ opacity: 0 }} aria-hidden />}
          </div>
        );
      })}
      <div className="absolute inset-0 z-[3] hero-shade-left" />
      <div className="absolute inset-0 z-[3] hero-shade-top" />
      <div className="absolute inset-x-0 bottom-0 h-[55%] z-[3] hero-shade-bottom" />
      <div className="hero-grain z-[3]" />

      <div key={slide.id} className="absolute left-[54px] top-[13%] max-w-[560px] hero-text z-[4] hidden sm:block" data-testid="hero-text">
        {slide.subtitle && <OriginalBadge kind={slide.subtitle.replace(/^CLAX\s*/i, '')} className="!text-[12px] !tracking-[0.14em] [&_img]:!h-[12px]" />}
        <div className="mt-[10px]"><HeroTitle title={slide.title} /></div>
        <p className="mt-[14px] text-[17px] leading-[1.3] text-white/95 max-w-[400px] whitespace-pre-line" data-testid="hero-description">{slide.description}</p>
        <div className="mt-[14px] flex items-center gap-3 text-[14px] text-white/85">
          {meta.map((m, i) => (<React.Fragment key={`${m}-${i}`}>{i > 0 && <span className="w-[3px] h-[3px] rounded-full bg-white/60" />}<span>{m}</span></React.Fragment>))}
          {c.duration && <><span className="w-[3px] h-[3px] rounded-full bg-white/60" /><span>{fmtDuration(c.duration)}</span></>}
        </div>
        <div className="mt-[18px] flex items-center gap-[14px]">
          <button className="btn-play" onClick={onPlay} data-testid="hero-play"><Play size={16} fill="currentColor" /> Play Now</button>
          <button className="btn-ghost" onClick={() => openDetail(c.id)} data-testid="hero-more-info"><Info size={18} /> More Info</button>
        </div>
      </div>

      <div key={`m-${slide.id}`} className="absolute inset-x-5 bottom-8 sm:hidden hero-text z-[4]">
        {slide.subtitle && <OriginalBadge kind={slide.subtitle.replace(/^CLAX\s*/i, '')} className="!text-[11px]" />}
        <div className="mt-2"><HeroTitle title={slide.title} size={0.6} /></div>
        <p className="mt-2 text-[14px] text-white/90 whitespace-pre-line">{slide.description}</p>
        <div className="mt-4 flex gap-3">
          <button className="btn-play flex-1 justify-center !min-w-0" onClick={onPlay}><Play size={16} fill="currentColor" /> Play</button>
          <button className="btn-ghost flex-1 justify-center" onClick={() => openDetail(c.id)}><Info size={18} /> Info</button>
        </div>
      </div>

      {n > 1 && (
        <>
          <button className="hero-arrow left hidden sm:flex" onClick={() => back(true)} aria-label="Previous" data-testid="hero-prev"><ChevronLeft size={22} /></button>
          <button className="hero-arrow right hidden sm:flex" onClick={() => next(true)} aria-label="Next" data-testid="hero-next"><ChevronRight size={22} /></button>
          <div className="absolute bottom-[16px] left-0 right-0 z-[5] flex justify-center gap-[6px]" data-testid="hero-indicators">
            {items.map((s, i) => (
              <button key={s.id} onClick={() => goTo(i, true)} aria-label={`Slide ${i + 1}`} aria-current={i === idx} className={`hero-dot ${i === idx ? 'on' : ''}`} style={{ width: i === idx ? 26 : 18 }} data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
